import { isUnsplashImage } from '../unsplash/utils';

interface getImageTypeToShowProps {
	imageSrc?: string;
}

export const getImageTypeToShow = ({ imageSrc }: getImageTypeToShowProps) => {
	const isFileId = Boolean(imageSrc?.match(/([0-9a-fA-F-]{36})/));
	const showCustomUploadedImage = !!imageSrc && isFileId;
	const showUnsplashImage = !showCustomUploadedImage && isUnsplashImage(imageSrc);
	const showExternalSiteImage = !!imageSrc && !showCustomUploadedImage && !showUnsplashImage;

	return {
		showUnsplashImage,
		showExternalSiteImage,
		showCustomUploadedImage,
		hasImage: showUnsplashImage || showCustomUploadedImage || showExternalSiteImage,
	};
};

/**
 * Checks if a URL points to a valid image by creating an Image element and testing if it loads
 * @param url - The URL to validate as an image
 * @returns Promise<boolean> - True if the URL is a valid image, false otherwise
 */
export const isImageURL = (url: string): Promise<boolean> => {
	return new Promise((resolve) => {
		// Creating an image element in memory to verify if the URL is a valid image
		const img = new Image();
		img.onload = () => resolve(true);
		img.onerror = () => resolve(false);
		img.src = url;
	});
};

/**
 * Normalizes a URL by adding the appropriate protocol if missing
 * @param url - The URL to normalize
 * @returns string - The normalized URL with protocol
 */
export const normalizeUrl = (url: string): string => {
	const trimmedUrl = url.trim();

	// If it already has a protocol, return as is
	if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
		return trimmedUrl;
	}

	// If it starts with //, add https:
	if (trimmedUrl.startsWith('//')) {
		return `https:${trimmedUrl}`;
	}

	// Otherwise, add https:// (default to secure)
	return `https://${trimmedUrl}`;
};

/**
 * Validates if a string is a valid URL format
 * @param url - The URL string to validate
 * @returns boolean - True if the URL is valid, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
	const trimmedUrl = url.trim();

	// Handle empty strings
	if (!trimmedUrl) {
		return false;
	}

	// Check if it's already a valid URL with protocol
	if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
		try {
			new URL(trimmedUrl);
			return true;
		} catch {
			return false;
		}
	}

	// For URLs without protocol, check if they look like valid domains
	// This prevents things like "invalid-url" from being normalized to "https://invalid-url"
	if (trimmedUrl.startsWith('//')) {
		// Protocol-relative URLs should have a valid domain after //
		const domainPart = trimmedUrl.substring(2);
		return domainPart.includes('.') && !domainPart.includes(' ');
	}

	// For URLs without protocol, they should look like valid domains
	// Must contain a dot (for domain) and no spaces
	if (trimmedUrl.includes('.') && !trimmedUrl.includes(' ')) {
		// Basic domain validation - should have at least one dot and no spaces
		return true;
	}

	return false;
};
